export type ReplyOperation = 'reply' | 'reply_all'
export type ReplyAttempt = {
  id: string
  fingerprint: string
  sourceMessageId: string
  operation: ReplyOperation
  senderMailboxId: string
  senderEmail?: string
  recipients?: EmailRecipients
}
export type ReplyAttemptResult = {
  ok: boolean
  attemptId: string
  attemptState: string
  providerAccepted: boolean
  acceptanceUnknown: boolean
  persistencePending: boolean
  safeToRetry: boolean
  writesPerformed: boolean
  sourceMessageId?: string
  mailboxId?: string
  attemptConflict?: boolean
  recipientRoleMismatch?: boolean
  requestKind?: string
  contactId?: string
  capturedSenderEmail?: string
  capturedToRecipients?: string[]
  capturedCcRecipients?: string[]
  capturedBccRecipients?: string[]
}
const prefix = 'avertsignal:reply-attempt:'
const key = (scope: string, thread: string) => `${prefix}${encodeURIComponent(scope)}:${encodeURIComponent(thread)}`
const valid = (value: unknown): value is ReplyAttempt => {
  const attempt = value as Partial<ReplyAttempt> | null
  return Boolean(attempt && typeof attempt.id === 'string' && /^[a-f0-9-]{36}$/i.test(attempt.id)
    && typeof attempt.fingerprint === 'string' && /^[a-f0-9]{64}$/i.test(attempt.fingerprint)
    && typeof attempt.sourceMessageId === 'string' && typeof attempt.senderMailboxId === 'string'
    && ['reply', 'reply_all'].includes(attempt.operation || '')
    && (attempt.senderEmail===undefined||typeof attempt.senderEmail==='string')
    && (attempt.recipients===undefined||validEmailRecipients(attempt.recipients)))
}
export function readReplyAttempt(scope: string, thread: string): ReplyAttempt | undefined {
  try { const value: unknown = JSON.parse(localStorage.getItem(key(scope, thread)) || 'null'); return valid(value) ? value : undefined } catch { return undefined }
}
// Persist only retry identity and record IDs, never reply text or credentials.
// If this cannot be durable, block sending instead of losing retry protection.
export function saveReplyAttempt(scope: string, thread: string, attempt: ReplyAttempt) {
  localStorage.setItem(key(scope, thread), JSON.stringify(attempt))
  if (readReplyAttempt(scope, thread)?.id !== attempt.id) throw Error('reply_attempt_storage_unavailable')
}
export function purgeReplyAttempts(scope: string) {
  const start = `${prefix}${encodeURIComponent(scope)}:`
  try { for (const name of Object.keys(localStorage)) if (name.startsWith(start)) localStorage.removeItem(name) } catch { /* No protected reply content is stored here. */ }
}
export async function replyFingerprint(operation: ReplyOperation, sourceMessageId: string, senderMailboxId: string, comment: string, recipients?: EmailRecipients) {
  const bytes = new TextEncoder().encode(JSON.stringify(recipients ? [operation, sourceMessageId, senderMailboxId, comment, recipients] : [operation, sourceMessageId, senderMailboxId, comment]))
  const digest = await crypto.subtle.digest('SHA-256', bytes)
  return [...new Uint8Array(digest)].map(byte => byte.toString(16).padStart(2, '0')).join('')
}
export function clearReplyAttempt(scope:string,thread:string){try{localStorage.removeItem(key(scope,thread))}catch{/* Continue clearing the in-memory protected state. */}}

export type EmailRecipients={to:string[];cc:string[];bcc:string[]}
export function validEmailRecipients(value:unknown):value is EmailRecipients{
  const recipients=value as EmailRecipients|null
  return Boolean(recipients&&['to','cc','bcc'].every(field=>Array.isArray(recipients[field as keyof EmailRecipients])&&recipients[field as keyof EmailRecipients].length<=50&&recipients[field as keyof EmailRecipients].every(address=>typeof address==='string'&&/^[^\s<>@,;]+@[^\s<>@,;]+\.[^\s<>@,;]+$/.test(address))))
}
export type InboxEmailDraft={senderMailboxId:string;to:string;cc:string;bcc:string;subject:string;body:string;contactId:string;replyMode?:ReplyOperation;attempt?:{id:string;fingerprint:string;senderEmail:string;recipients:EmailRecipients}}
const draftKey=(scope:string,id:string)=>`avertsignal:new-email:${encodeURIComponent(scope)}:${encodeURIComponent(`inbox:${id}`)}`
export function readInboxEmailDraft(scope:string,id:string):InboxEmailDraft|undefined{
  try{const draft=JSON.parse(localStorage.getItem(draftKey(scope,id))||'null') as InboxEmailDraft|null
    if(!draft||['senderMailboxId','to','cc','bcc','subject','body','contactId'].some(field=>typeof draft[field as keyof InboxEmailDraft]!=='string'))return
    if(draft.replyMode!==undefined&&!['reply','reply_all'].includes(draft.replyMode))return
    if(draft.attempt&&(!/^[a-f0-9-]{36}$/i.test(draft.attempt.id)||!/^[a-f0-9]{64}$/i.test(draft.attempt.fingerprint)||typeof draft.attempt.senderEmail!=='string'||!validEmailRecipients(draft.attempt.recipients)))return
    return draft
  }catch{return}
}
export function saveInboxEmailDraft(scope:string,id:string,draft:InboxEmailDraft){
  localStorage.setItem(draftKey(scope,id),JSON.stringify(draft))
  if(JSON.stringify(readInboxEmailDraft(scope,id))!==JSON.stringify(draft))throw Error('email_attempt_storage_unavailable')
}
export function clearInboxEmailDraft(scope:string,id:string){try{localStorage.removeItem(draftKey(scope,id))}catch{/* Continue clearing the in-memory protected state. */}}
export function purgeInboxEmailDrafts(scope:string){
  const start=`avertsignal:new-email:${encodeURIComponent(scope)}:${encodeURIComponent('inbox:')}`
  try{for(const name of Object.keys(localStorage))if(name.startsWith(start))localStorage.removeItem(name)}catch{/* Clear in-memory protected state even if storage fails. */}
}
export function parseEmailRecipients(to:string,cc='',bcc=''):EmailRecipients{
  const seen=new Set<string>()
  const normalize=(value:string)=>value.split(/[;,]/).map(address=>address.trim().toLowerCase()).filter(Boolean).map(address=>{
    const parts=address.split('@'),local=parts[0]
    if(address.length>254||/[\s<>(),;:\r\n]/.test(address)||parts.length!==2||/[/?#\\%]/.test(parts[1])||!/^[a-z0-9.!#$%&'*+\-/=?^_`{|}~]+$/i.test(local)||local.startsWith('.')||local.endsWith('.')||local.includes('..'))throw Error(`Invalid email address: ${address}`)
    let domain:string
    try{domain=new URL(`https://${parts[1]}`).hostname}catch{throw Error(`Invalid email address: ${address}`)}
    if(!domain.includes('.')||domain.split('.').some(part=>!/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i.test(part)))throw Error(`Invalid email address: ${address}`)
    return `${local}@${domain}`
  }).filter(address=>{
    if(seen.has(address))return false;seen.add(address);return true
  })
  const result={to:normalize(to).sort(),cc:normalize(cc).sort(),bcc:normalize(bcc).sort()}
  if(!result.to.length)throw Error('Enter at least one To recipient.')
  if(seen.size>50)throw Error('Use no more than 50 recipients.')
  return result
}
export async function inboxEmailFingerprint(actorId:string,workspaceId:string,draft:InboxEmailDraft,recipients:EmailRecipients){
  const bytes=new TextEncoder().encode(JSON.stringify([actorId,workspaceId,draft.senderMailboxId,draft.contactId,recipients,draft.subject.trim(),draft.body.trim()]))
  return [...new Uint8Array(await crypto.subtle.digest('SHA-256',bytes))].map(byte=>byte.toString(16).padStart(2,'0')).join('')
}
export function replyAttemptLabel(result: ReplyAttemptResult): string {
  if(result.recipientRoleMismatch)return 'Recovery mismatch: provider To/Cc/Bcc roles differ from the captured attempt. Acceptance of this exact intent is unverified; do not resend.'
  if(result.attemptState==='NOT_RESERVED')return 'No durable reply reservation found. Retry this attempt.'
  if (result.providerAccepted === true && result.acceptanceUnknown === false) return result.persistencePending ? 'Provider accepted; CRM history reconciliation pending.' : 'Provider accepted; CRM history recorded.'
  if (result.acceptanceUnknown === true) return 'Send outcome uncertain. Check status; do not resend.'
  if (result.safeToRetry === true) return 'Send failed before acceptance. Retry the unchanged attempt.'
  return 'Reply pending. Check status before another send.'
}
export function parseReplyAttemptResult(value: unknown): ReplyAttemptResult {
  const result = value as Partial<ReplyAttemptResult> | null
  if (!result || typeof result.ok !== 'boolean' || typeof result.attemptId !== 'string' || typeof result.attemptState !== 'string'
    || ['providerAccepted', 'acceptanceUnknown', 'persistencePending', 'safeToRetry'].some(field => typeof result[field as keyof ReplyAttemptResult] !== 'boolean')
    || !['RESERVED','DRAFT_READY','SUBMITTING','UNCERTAIN','PROVIDER_ACCEPTED','RECORDED','PRE_SEND_FAILED'].includes(result.attemptState)
    || result.providerAccepted !== ['PROVIDER_ACCEPTED','RECORDED'].includes(result.attemptState)
    || result.acceptanceUnknown !== ['SUBMITTING','UNCERTAIN'].includes(result.attemptState)
    || result.persistencePending !== (result.attemptState === 'PROVIDER_ACCEPTED')
    || (result.recipientRoleMismatch !== undefined && (typeof result.recipientRoleMismatch !== 'boolean' || (result.recipientRoleMismatch && (!result.acceptanceUnknown || result.providerAccepted || result.safeToRetry))))
    || result.safeToRetry !== (result.attemptState === 'PRE_SEND_FAILED')) throw Error('reply_outcome_unverified')
  return result as ReplyAttemptResult
}
